
import React from 'react';
import { Ambassador, ApplicationStatus } from '../types';
import { Badge } from './Badge';

interface ApplicantCardProps {
  ambassador: Ambassador;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onClick: (ambassador: Ambassador) => void;
  onApprove: (id: string) => void;
  onOnboard: (id: string) => void;
  onArchive: (id: string) => void;
  onReverseApproval: (id: string) => void;
}

export const ApplicantCard: React.FC<ApplicantCardProps> = ({ 
  ambassador, 
  isSelected = false,
  onToggleSelect,
  onClick, 
  onApprove, 
  onOnboard, 
  onArchive, 
  onReverseApproval 
}) => {
  const hasSocials = ambassador.instagramHandle || ambassador.tiktokHandle;
  const hasCredentials = ambassador.ambassadorCode && ambassador.password;

  return (
    <div 
      className={`group relative bg-white border ${isSelected ? 'border-purple-600 ring-2 ring-purple-100 shadow-lg' : 'border-gray-100'} rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col justify-between h-full`}
      onClick={() => onClick(ambassador)}
    >
      {onToggleSelect && (
        <div 
          className="absolute top-4 left-4 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => onToggleSelect()}
            className="w-5 h-5 rounded-md border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer transition-all"
          />
        </div>
      )}

      <div className={onToggleSelect ? 'pl-8' : ''}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 font-bold text-lg group-hover:rotate-3 transition-transform overflow-hidden">
                {ambassador.profilePhotoUrl ? (
                  <img src={ambassador.profilePhotoUrl} className="w-full h-full object-cover" alt="Profile" />
                ) : (
                  `${ambassador.firstName[0]}${ambassador.lastName[0]}`
                )}
              </div>
              <div className="absolute -top-1 -right-1 w-7 h-7 bg-purple-600 border-4 border-purple-50 rounded-full flex items-center justify-center text-[8px] text-white font-black shadow-sm">
                {ambassador.onboardingScore}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 tracking-tight leading-none mb-1">
                {ambassador.firstName} {ambassador.lastName}
              </h3>
              <p className="text-[11px] text-gray-400">{ambassador.location}</p>
            </div>
          </div>
          <Badge status={ambassador.status} />
        </div>

        <div className="mb-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-1">Background</p>
          <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed h-[2.5rem]">
            {ambassador.reason}
          </p>
        </div>

        {hasCredentials && (
          <div className="mb-4 p-3 bg-purple-50 rounded-2xl border border-purple-100 flex items-center justify-between">
            <div className="flex-1">
              <p className="text-[7px] font-black uppercase text-purple-400">Portal Code</p>
              <p className="text-[11px] font-bold text-purple-900 font-mono tracking-tight">{ambassador.ambassadorCode}</p>
            </div>
            <div className="flex-1 text-right">
              <p className="text-[7px] font-black uppercase text-purple-400">Portal Pass</p>
              <p className="text-[11px] font-bold text-purple-900 font-mono tracking-tight">{ambassador.password}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-purple-50">
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-gray-300">Applied</p>
            <p className="text-[11px] text-gray-500 font-medium">{ambassador.appliedDate}</p>
          </div>
          {hasSocials && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest text-gray-300">Socials</p>
              <div className="flex flex-col gap-1 mt-0.5">
                {ambassador.instagramHandle && (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-2.5 h-2.5 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.332 3.608 1.308.975.975 1.245 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.332 2.633-1.308 3.608-.975.975-2.242 1.245-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.332-3.608-1.308-.975-.975-1.245-2.242-1.308-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.332-2.633 1.308-3.608.975-.975 2.242-1.245 3.608-1.308 1.266-.058 1.646-.07 4.85-.07zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948s.014 3.667.072 4.947c.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072s3.667-.014 4.947-.072c4.358-.2 6.78-2.618 6.98-6.98.058-1.281.072-1.689.072-4.948s-.014-3.667-.072-4.947c-.2-4.358-2.618-6.78-6.98-6.98-1.281-.058-1.689-.072-4.948-.072zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    <span className="text-[10px] text-purple-600 font-bold truncate leading-none">{ambassador.instagramHandle}</span>
                  </div>
                )}
                {ambassador.tiktokHandle && (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-2.5 h-2.5 text-gray-900" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                    <span className="text-[10px] text-gray-900 font-bold truncate leading-none">{ambassador.tiktokHandle}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex gap-2" onClick={(e) => e.stopPropagation()}>
        {ambassador.status === ApplicationStatus.NEW && (
          <>
            <button 
              onClick={() => onApprove(ambassador.id)}
              className="flex-1 bg-purple-600 text-white rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-purple-100 hover:bg-purple-500 transition-all"
            >
              Approve & Send Welcome
            </button>
            <button 
              onClick={() => onArchive(ambassador.id)}
              className="px-4 border border-gray-100 text-gray-400 rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all"
            >
              Archive
            </button>
          </>
        )}
        {ambassador.status === ApplicationStatus.WELCOME_SENT && (
          <>
            <button 
              onClick={() => onOnboard(ambassador.id)}
              className="flex-1 bg-green-600 text-white rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-100 hover:bg-green-500 transition-all"
            >
              Signed/Onboarded
            </button>
            <button 
              onClick={() => onReverseApproval(ambassador.id)}
              className="px-4 border border-purple-200 text-purple-600 rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-all"
            >
              Undo
            </button>
          </>
        )}
        {ambassador.status === ApplicationStatus.SIGNED_ONBOARDED && (
          <>
            <button 
               onClick={() => onArchive(ambassador.id)}
               className="flex-1 bg-gray-900 text-white rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all"
            >
              Archive Record
            </button>
            <button 
              onClick={() => onReverseApproval(ambassador.id)}
              className="px-4 border border-purple-200 text-purple-600 rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-purple-50 transition-all"
            >
              Undo
            </button>
          </>
        )}
        {ambassador.status === ApplicationStatus.ARCHIVED && (
          <button 
             onClick={() => onReverseApproval(ambassador.id)}
             className="w-full bg-black text-white rounded-full py-2.5 text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all"
          >
            Re-Activate
          </button>
        )}
      </div>
    </div>
  );
};
